import React, { useEffect, useState } from "react";
import { Button, Input, message, Select, Space } from "antd";
import axios from "axios";
import {
    countryCodeOptions,
    industryOptions,
    initialErrorList,
    inputInitialValues,
} from "../data/formOptions";
import { ENQUIRY_ENDPOINT } from "../config/api";

export const ContactForm = () => {
    const [loading, setLoading] = useState(false);
    const [errorList, setErrorList] = useState(initialErrorList);
    const [inputReq, setInputReq] = useState(inputInitialValues);
    const [yearStartedOptions, setYearStartedOptions] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        const years = [];
        const currentYear = new Date().getFullYear();
        for (let year = 1950; year <= currentYear; year++) {
            years.push({ value: String(year), label: String(year) });
        }
        setYearStartedOptions(years);
    }, []);

    const resetForm = () => {
        setInputReq(inputInitialValues);
        setErrorList(initialErrorList);
    };

    const handleChangeValues = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
        setErrorList({ ...errorList, [key]: "" });
        setInputReq({ ...inputReq, [key]: e.target.value });
    };

    const handleSelectValue = (key: keyof typeof inputInitialValues, value: string) => {
        setInputReq({ ...inputReq, [key]: value });
    };

    const handleSubmit = async () => {
        const errors = { ...initialErrorList };
        let isErrorsFound = false;

        if (inputReq.firstName === "") {
            errors.firstName = "This field can't be empty";
            isErrorsFound = true;
        }
        if (inputReq.lastName === "") {
            errors.lastName = "This field can't be empty";
            isErrorsFound = true;
        }
        if (inputReq.mobile === "") {
            errors.mobile = "This field can't be empty";
            isErrorsFound = true;
        }
        if (inputReq.email === "") {
            errors.email = "This field can't be empty";
            isErrorsFound = true;
        } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(inputReq.email)) {
            errors.email = "Please enter valid email";
            isErrorsFound = true;
        }

        if (isErrorsFound) {
            setErrorList(errors);
            message.error("Invalid request, kindly check the inputs");
            return;
        }

        try {
            setLoading(true);
            const data = { ...inputReq, createdDate: new Date().toISOString() };
            const resp = await axios.post(ENQUIRY_ENDPOINT, data);
            if (resp) {
                message.success("Your request has been submitted successfully");
                resetForm();
            }
        } catch (e) {
            if (axios.isAxiosError(e) && e.response?.data?.message) {
                message.error(String(e.response.data.message));
            } else {
                message.error("Form submission failed, try again later");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="contact-form" id="enquiry-form">
            <header className="contact-form__header">
                <h2 className="contact-form__title">We&apos;re Here to Help</h2>
            </header>

            <div className="contact-form__body">
                <section className="contact-form__section">
                    <h3 className="contact-form__section-title">Contact Details</h3>

                    <div className="contact-form__row">
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-first-name">
                                First Name
                            </label>
                            <Input
                                id="contact-first-name"
                                className="contact-form__input"
                                placeholder="First Name"
                                value={inputReq.firstName}
                                onChange={(e) => handleChangeValues(e, "firstName")}
                            />
                            {errorList.firstName && (
                                <span className="contact-form__error">{errorList.firstName}</span>
                            )}
                        </div>
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-last-name">
                                Last Name
                            </label>
                            <Input
                                id="contact-last-name"
                                className="contact-form__input"
                                placeholder="Last Name"
                                value={inputReq.lastName}
                                onChange={(e) => handleChangeValues(e, "lastName")}
                            />
                            {errorList.lastName && (
                                <span className="contact-form__error">{errorList.lastName}</span>
                            )}
                        </div>
                    </div>

                    <div className="contact-form__row">
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-mobile">
                                WhatsApp Number
                            </label>
                            <Space.Compact className="contact-form__phone-group">
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    className="contact-form__country-code"
                                    value={inputReq.countryCode}
                                    options={countryCodeOptions}
                                    onChange={(val) => handleSelectValue("countryCode", val)}
                                />
                                <Input
                                    id="contact-mobile"
                                    className="contact-form__input contact-form__input--phone"
                                    placeholder="Personal WhatsApp Number"
                                    value={inputReq.mobile}
                                    onChange={(e) => handleChangeValues(e, "mobile")}
                                />
                            </Space.Compact>
                            {errorList.mobile && (
                                <span className="contact-form__error">{errorList.mobile}</span>
                            )}
                        </div>
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-email">
                                Work Email
                            </label>
                            <Input
                                id="contact-email"
                                className="contact-form__input"
                                placeholder="Work Email"
                                value={inputReq.email}
                                onChange={(e) => handleChangeValues(e, "email")}
                            />
                            {errorList.email && (
                                <span className="contact-form__error">{errorList.email}</span>
                            )}
                        </div>
                    </div>
                </section>

                <section className="contact-form__section">
                    <h3 className="contact-form__section-title">Company Details</h3>

                    <div className="contact-form__row">
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-company">
                                Company Name
                            </label>
                            <Input
                                id="contact-company"
                                className="contact-form__input"
                                placeholder="Company Name"
                                value={inputReq.companyName}
                                onChange={(e) => handleChangeValues(e, "companyName")}
                            />
                        </div>
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-website">
                                Website
                            </label>
                            <Input
                                id="contact-website"
                                className="contact-form__input"
                                placeholder="Website"
                                value={inputReq.website}
                                onChange={(e) => handleChangeValues(e, "website")}
                            />
                        </div>
                    </div>

                    <div className="contact-form__row">
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-location">
                                Location
                            </label>
                            <Input
                                id="contact-location"
                                className="contact-form__input"
                                placeholder="Location"
                                value={inputReq.location}
                                onChange={(e) => handleChangeValues(e, "location")}
                            />
                        </div>
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-designation">
                                Designation
                            </label>
                            <Input
                                id="contact-designation"
                                className="contact-form__input"
                                placeholder="Designation"
                                value={inputReq.designation}
                                onChange={(e) => handleChangeValues(e, "designation")}
                            />
                        </div>
                    </div>

                    <div className="contact-form__row">
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-industry">
                                Industry
                            </label>
                            <Select
                                id="contact-industry"
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                className="contact-form__select"
                                options={industryOptions}
                                placeholder="Industry"
                                value={inputReq.industry || undefined}
                                onChange={(val) => handleSelectValue("industry", val ?? "")}
                            />
                        </div>
                        <div className="contact-form__field">
                            <label className="contact-form__label" htmlFor="contact-year-started">
                                Year Started
                            </label>
                            <Select
                                id="contact-year-started"
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                className="contact-form__select"
                                options={yearStartedOptions}
                                placeholder="Year started"
                                value={inputReq.yearStarted || undefined}
                                onChange={(val) => handleSelectValue("yearStarted", val ?? "")}
                            />
                        </div>
                    </div>
                </section>
            </div>

            <div className="contact-form__actions">
                <Button className="contact-form__submit" loading={loading} onClick={handleSubmit}>
                    Submit
                </Button>
            </div>
        </div>
    );
};
